// src/features/events/BackupDialogs.js
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  LinearProgress,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  Paper,
} from "@mui/material";
import {
  Backup as BackupIcon,
  DeleteSweep as DeleteSweepIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import {
  selectBackupSettings,
  selectBackupRunning,
  triggerBackup,
  updateBackupSettings,
} from "./backupSlice";
import { getEvents, resetEvents } from "./eventsSlice";

// ═══════════════════════════════════════════════════════════
// 1. BACKUP ONLY DIALOG
// ═══════════════════════════════════════════════════════════
export function BackupConfirmDialog({ open, onClose }) {
  const dispatch = useDispatch();
  const running = useSelector(selectBackupRunning);

  const handleStart = async () => {
    await dispatch(triggerBackup(false));
    dispatch(resetEvents());
    dispatch(getEvents(1, true, null, 10));
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}
      >
        <BackupIcon color="primary" /> Backup Media Folder
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          This will upload your <strong>media folder</strong> to Google Drive in
          the background.
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>No events will be deleted.</strong> Backup only.
        </Alert>
        {running && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress sx={{ height: 8, borderRadius: 4 }} />
            <Typography
              variant="caption"
              sx={{ mt: 1, display: "block", color: "text.secondary" }}
            >
              Backing up in the background. You can close this window. Ticks
              will appear automatically when finished.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={running}>
          Cancel
        </Button>
        <Button
          onClick={handleStart}
          variant="contained"
          disabled={running}
          startIcon={<BackupIcon />}
        >
          {running ? "Backing up..." : "Start Backup"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════
// 2. BACKUP + DELETE DIALOG
// ═══════════════════════════════════════════════════════════
export function BackupDeleteConfirmDialog({ open, onClose }) {
  const dispatch = useDispatch();
  const running = useSelector(selectBackupRunning);
  const [step, setStep] = useState(1);

  useEffect(() => {
    if (!open) setStep(1);
  }, [open]);

  const handleFinalConfirm = async () => {
    await dispatch(triggerBackup(true));
    dispatch(resetEvents());
    dispatch(getEvents(1, true, null, 10));
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {step === 1 ? (
        <>
          <DialogTitle
            sx={{
              color: "#d32f2f",
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <WarningIcon /> Backup AND Delete?
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              This action will:
            </Typography>
            <ul>
              <li>
                <Typography variant="body2">
                  Upload media folder to Google Drive
                </Typography>
              </li>
              <li>
                <Typography variant="body2" sx={{ color: "#d32f2f", mt: 1 }}>
                  <strong>Delete all untagged events</strong> (events with no
                  species tags)
                </Typography>
              </li>
            </ul>
            <Alert severity="warning" sx={{ mt: 2 }}>
              <strong>Featured events are protected.</strong> Only untagged
              events will be deleted.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              onClick={() => setStep(2)}
              color="warning"
              variant="contained"
            >
              I Understand — Continue
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle
            sx={{
              color: "#d32f2f",
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <WarningIcon /> FINAL CONFIRMATION
          </DialogTitle>
          <DialogContent>
            <Alert severity="error" sx={{ mb: 2 }}>
              <strong>This action cannot be undone.</strong>
            </Alert>
            {running && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress
                  color="error"
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography
                  variant="caption"
                  sx={{ mt: 1, display: "block", color: "text.secondary" }}
                >
                  Backing up + deleting in background. This may take 10-30
                  minutes.
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStep(1)} disabled={running}>
              Go Back
            </Button>
            <Button
              onClick={handleFinalConfirm}
              color="error"
              variant="contained"
              disabled={running}
              startIcon={<DeleteSweepIcon />}
            >
              {running ? "Processing..." : `Yes, Backup & Delete`}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════
// 3. SETTINGS DIALOG
// ═══════════════════════════════════════════════════════════
export function BackupSettingsDialog({ open, onClose }) {
  const dispatch = useDispatch();
  const settings = useSelector(selectBackupSettings);
  const [enabled, setEnabled] = useState(false);
  const [days, setDays] = useState(3);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && settings) {
      setEnabled(settings.auto_backup_enabled);
      setDays(settings.auto_backup_interval_days);
    }
  }, [open, settings]);

  const handleSave = async () => {
    setSaving(true);
    await dispatch(
      updateBackupSettings({
        auto_backup_enabled: enabled,
        auto_backup_interval_days: days,
      }),
    );
    setSaving(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SettingsIcon color="primary" /> Auto Backup Settings
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure automatic backup + cleanup. The server will run this
          automatically every N days.
        </Typography>

        <Paper
          sx={{
            p: 2,
            mb: 3,
            bgcolor: enabled ? "#e8f5e9" : "#f5f5f5",
            borderRadius: 2,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                color="success"
              />
            }
            label={
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>
                  Enable Auto Backup
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Server will backup + delete untagged events automatically
                </Typography>
              </Box>
            }
          />
        </Paper>

        <Paper sx={{ p: 2, mb: 3, bgcolor: "#f5f5f5", borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
            Backup every N days
          </Typography>
          <TextField
            type="number"
            value={days}
            onChange={(e) =>
              setDays(Math.max(1, Math.min(30, parseInt(e.target.value) || 3)))
            }
            size="small"
            fullWidth
            helperText="Default: 3 days."
          />
        </Paper>

        {settings?.last_backup_at && (
          <Typography variant="caption" color="text.secondary">
            Last backup:{" "}
            <strong>
              {new Date(settings.last_backup_at).toLocaleString()}
            </strong>
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          startIcon={<CheckCircleIcon />}
        >
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
